import { useEffect, useState } from "react";
import {
  clearPending,
  loadPending,
  setReconnectIntent,
  type PendingMatch,
  type ReconnectGame,
} from "../lib/matchReconnect";
import GameIcon, { gameMeta } from "./GameIcon";

interface MatchReconnectCardProps {
  game: ReconnectGame;
  /** The currently-logged-in wallet — used to scope the card so only
   *  the account that owns the saved match sees it. */
  walletAddress: string | null;
  /** Asks the parent to navigate to the game route. The game page reads
   *  the reconnect intent on mount and calls the appropriate reconnect
   *  client function automatically. */
  onReconnect: (game: ReconnectGame) => void;
  /** Visual stack position so multiple games can stack their cards
   *  bottom-right without overlapping. 0 = bottommost. */
  stackIndex?: number;
}

function walletColors(addr: string | null | undefined) {
  if (!addr) return { from: "#444", to: "#222", initials: "?" };
  const h1 = parseInt(addr.slice(2, 8), 16) % 360;
  const h2 = (h1 + 120 + (parseInt(addr.slice(8, 12), 16) % 60)) % 360;
  return {
    from: `hsl(${h1} 70% 55%)`,
    to: `hsl(${h2} 65% 35%)`,
    initials: addr.slice(2, 4).toUpperCase(),
  };
}

function abbrev(addr: string | null | undefined): string {
  if (!addr) return "Anon";
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

const GAME_ICON_KIND: Record<ReconnectGame, "tower-defense" | "math"> = {
  td: "tower-defense",
  math: "math",
};

/**
 * Floating bottom-right card shown when the user has a live TD or Math
 * match they can rejoin (token + room id stored in localStorage by the
 * respective Colyseus client). Polls the store every 500 ms so a server-
 * side dispose (match_end / forfeit) clears the card within a beat.
 */
export default function MatchReconnectCard({
  game,
  walletAddress,
  onReconnect,
  stackIndex = 0,
}: MatchReconnectCardProps) {
  const [pending, setPending] = useState<PendingMatch | null>(null);

  useEffect(() => {
    const refresh = () => setPending(loadPending(game));
    refresh();
    const id = window.setInterval(refresh, 500);
    return () => window.clearInterval(id);
  }, [game]);

  if (!pending) return null;
  if (!walletAddress) return null;
  if ((pending.walletAddress ?? "").toLowerCase() !== walletAddress.toLowerCase()) {
    return null;
  }
  // The pending blob is saved on JOIN (so a drop mid-search is recoverable
  // internally), but the reconnect card must only represent a match that
  // actually STARTED. `matchStartedAtMs` is null while still in the
  // matchmaking queue — don't surface the card until an opponent is found.
  if (!pending.matchStartedAtMs) return null;

  const meta = gameMeta(GAME_ICON_KIND[game]);
  const stakeDollars = Number(pending.stake) || 0;
  const totalPot = stakeDollars * 2;
  const oppCol = walletColors(pending.opponentWallet);

  const handleClick = () => {
    setReconnectIntent(game);
    onReconnect(game);
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    clearPending(game);
    setPending(null);
  };

  return (
    <div
      className="fixed right-5 z-[90] w-[336px] rounded-2xl overflow-hidden"
      style={{
        bottom: 20 + stackIndex * 168,
        background: "linear-gradient(160deg, rgba(26,26,26,0.97) 0%, rgba(15,15,15,0.97) 100%)",
        border: `1px solid ${meta.accent}55`,
        boxShadow: `0 12px 40px ${meta.accent}38, 0 0 0 1px ${meta.accent}15`,
        backdropFilter: "blur(14px)",
      }}
    >
      <div
        className="flex items-center justify-between px-4 py-2.5 border-b border-white/5"
        style={{ background: `linear-gradient(90deg, ${meta.accent}22 0%, transparent 70%)` }}
      >
        <div className="flex items-center gap-2">
          <span
            className="flex items-center justify-center w-7 h-7 rounded-lg"
            style={{ background: `${meta.accent}25`, color: meta.accent }}
          >
            <GameIcon game={GAME_ICON_KIND[game]} className="w-4 h-4" />
          </span>
          <div className="flex flex-col leading-tight">
            <span className="text-[11px] font-bold tracking-wider text-white/95 uppercase">
              {meta.label}
            </span>
            <span className="flex items-center gap-1 text-[9px] uppercase tracking-widest text-white/40">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Live match
            </span>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="text-white/30 hover:text-white/70 text-[11px] uppercase tracking-wider px-1 transition"
          title="Forfeit and dismiss"
        >
          ✕
        </button>
      </div>

      <div className="px-4 py-4 flex items-center gap-4">
        <button
          onClick={handleClick}
          className="relative w-[58px] h-[58px] flex-shrink-0 group focus:outline-none"
          aria-label="Reconnect"
        >
          <span
            className="absolute inset-1.5 rounded-full flex items-center justify-center text-white font-bold text-[12px] tracking-wider transition-transform group-hover:scale-105 group-active:scale-95"
            style={{
              background: `linear-gradient(140deg, ${meta.accent} 0%, ${meta.accent2} 100%)`,
              boxShadow: `0 4px 14px ${meta.accent}66`,
            }}
          >
            JOIN
          </span>
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-1.5 mb-1">
            <span className="text-2xl font-extrabold text-white tracking-tight">
              ${totalPot.toFixed(2)}
            </span>
            <span className="text-[10px] uppercase tracking-widest text-white/40 font-semibold">
              pot
            </span>
          </div>
          <div className="flex items-center gap-2 mb-3">
            {pending.opponentWallet ? (
              <>
                <div
                  className="w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center text-[8px] font-bold text-white/95"
                  style={{
                    background: `linear-gradient(140deg, ${oppCol.from}, ${oppCol.to})`,
                  }}
                >
                  {oppCol.initials}
                </div>
                <span className="font-mono text-[11px] text-white/60 truncate">
                  vs {abbrev(pending.opponentWallet)}
                </span>
              </>
            ) : (
              <span className="font-mono text-[11px] text-white/60 truncate">
                Waiting for opponent
              </span>
            )}
          </div>
          <button
            onClick={handleClick}
            className="w-full py-2 rounded-lg text-white text-xs font-bold uppercase tracking-wider transition-all hover:shadow-lg active:scale-[0.98]"
            style={{
              background: `linear-gradient(90deg, ${meta.accent} 0%, ${meta.accent2} 100%)`,
              boxShadow: `0 4px 14px ${meta.accent}40`,
            }}
          >
            Reconnect
          </button>
        </div>
      </div>
    </div>
  );
}
